import type { AbstractIntlMessages } from 'next-intl';

import common from '../messages/en/common.json';

import { mergeMessages } from './merge';

/** The parts of next-intl's `IntlError` used here; keeps this file free of next-intl runtime. */
export interface MessageError {
  code: string;
  message: string;
}

/** Raised by next-intl when it runs without a provider; expected noise, never logged. */
const IGNORED_ERROR_CODES: ReadonlySet<string> = new Set(['ENVIRONMENT_FALLBACK']);

export const isIgnoredError = (error: MessageError): boolean => IGNORED_ERROR_CODES.has(error.code);

/** The only error code whose fallback is the English text: the key is not in the messages. */
const MISSING_MESSAGE = 'MISSING_MESSAGE';

/** ICU arguments (`{name}`) or rich-text tags (`<link>`). */
const ICU_SYNTAX = /[{}<]/;

/** `namespace.key`, or just `key` when there is no namespace. */
export const formatMessagePath = (namespace: string | undefined, key: string): string =>
  [namespace, key].filter((part) => part !== undefined && part !== '').join('.');

/** Finds the string at a dotted path in a catalog; `undefined` for objects or gaps. */
export const lookupMessage = (
  catalog: AbstractIntlMessages | undefined,
  path: string
): string | undefined => {
  let node: AbstractIntlMessages | string | undefined = catalog;
  for (const part of path.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
};

/**
 * English catalogs registered by shared packages (and this package's `common`).
 * It backs components rendered where no `I18nProvider` exists (apps that are not
 * wired for i18n, Storybook, tests) and keys an app did not ship to the client.
 */
let registered: AbstractIntlMessages = common;

/**
 * Registers a package's English catalog. Call it at import time with a static
 * import of the English JSON, so it is in every bundle that uses the package.
 * Deep-merged; registering the same catalog twice is harmless.
 */
export const registerFallbackMessages = (messages: AbstractIntlMessages): void => {
  registered = mergeMessages(registered, messages);
};

/** The registered English catalogs. */
export const getFallbackMessages = (): AbstractIntlMessages => registered;

export interface FallbackInput {
  /** The error next-intl reports; only `MISSING_MESSAGE` falls back to English. */
  error?: MessageError;
  namespace: string | undefined;
  key: string;
  /** English catalog, when available (server only). */
  english?: AbstractIntlMessages;
  /** Registered package catalogs; defaults to the module registry. */
  registry?: AbstractIntlMessages;
}

/**
 * What to render when a message cannot be resolved. For a missing key: the English text
 * when the key exists there, else the registered package English, else an empty string.
 * For any other error, and for English text with arguments or tags (no values to fill
 * in): an empty string. A raw key path, ICU source or error marker must never reach the
 * UI, in any environment.
 */
export const selectMessageFallback = ({
  error,
  namespace,
  key,
  english,
  registry = registered,
}: FallbackInput): string => {
  // Any other error (a missing value, bad values) means the message exists but cannot be
  // formatted; its English text would be the raw ICU source, e.g. `{name} joined {team}`.
  if (error && error.code !== MISSING_MESSAGE) return '';
  const path = formatMessagePath(namespace, key);
  const text = lookupMessage(english, path) ?? lookupMessage(registry, path) ?? '';
  // The fallback has no values to fill in, so a message with arguments or tags renders empty.
  return ICU_SYNTAX.test(text) ? '' : text;
};

/**
 * One-line report, e.g. `[i18n] MISSING_MESSAGE pickleball.x.y (es)`. next-intl
 * puts the path in backticks in the error message; without it the whole message
 * is used.
 */
export const formatMessageReport = (error: MessageError, locale: string): string => {
  const path = /`([^`]+)`/.exec(error.message)?.[1] ?? error.message;
  return `[i18n] ${error.code} ${path} (${locale})`;
};

/**
 * Returns a function that is `true` the first time it sees an id and `false`
 * after, so a missing string is reported once, not on every render. Stops
 * remembering past `limit` ids to bound memory (it then reports nothing new).
 */
export const createOnceGate = (limit = 1000) => {
  const seen = new Set<string>();
  return (id: string): boolean => {
    if (seen.has(id) || seen.size >= limit) return false;
    seen.add(id);
    return true;
  };
};
